// ProbarEnEscritorioModal — la pantalla que abre "Probar" en el cartel
// "Probá Atlas en tu computadora" (ADR-2026-08-25-cartel-probar-atlas-en-escritorio).
//
// El cartel que lo dispara sólo existe en teléfono, así que esta pantalla se
// diseña para teléfono: ocupa todo el alto y sólo a partir de `sm:` se comporta
// como un modal centrado (que es como se la ve en `/banners-dev`).
//
// No hay botón de "ir": el usuario está justamente en el dispositivo equivocado
// y un `<a href>` a la app lo devolvería a donde ya está. Por eso el cierre es el
// link escrito para que lo lea y lo tipee en la compu.

import React from "react";
import { createPortal } from "react-dom";
import { X, Monitor, Sparkles, Zap } from "lucide-react";

/**
 * Imagen de la pantalla. Hoy es un placeholder vectorial; cuando llegue la
 * definitiva se cambia SÓLO esta constante (y se borra el `.svg` de
 * `public/images`). Es un asset local, así que va con `<img>` común y no con
 * `CachedImg` — ese existe para URLs remotas / de Supabase.
 */
const IMAGEN = "/images/probar-en-compu.svg";

/** El link, en un solo lugar: aparece en el copy y es lo único accionable. */
const LINK = "app.estudio-atlas.com";

/** Una razón de la lista (ícono + título + explicación). */
const Razon: React.FC<{
  icono: React.ReactNode;
  titulo: string;
  detalle: string;
}> = ({ icono, titulo, detalle }) => (
  <li className="flex items-start gap-3">
    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#EBEFFE] text-brand-primary">
      {icono}
    </span>
    <div className="min-w-0">
      <p className="text-[15px] font-semibold text-brand-black/90">{titulo}</p>
      <p className="text-[14px] leading-snug text-[#3C3C43]/60">{detalle}</p>
    </div>
  </li>
);

export const ProbarEnEscritorioModal: React.FC<{
  open: boolean;
  /** Cierra la pantalla. El consumidor decide qué más hacer (hoy: dar el cartel
   *  por visto para que no vuelva). */
  onClose: () => void;
}> = ({ open, onClose }) => {
  if (!open) return null;

  return createPortal(
    // Portal a `document.body`: el panel de contenido del desktop es `position:
    // fixed`, así que un overlay montado dentro de la página queda POR DEBAJO de
    // la sidebar.
    <div
      className="fixed inset-0 z-[10000] flex items-stretch justify-center bg-black/50 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full flex-col bg-white shadow-2xl sm:h-auto sm:max-h-[85vh] sm:max-w-lg sm:rounded-[14px]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Probá Atlas en tu computadora"
      >
        <div className="flex shrink-0 items-center justify-end px-3 pt-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 transition-colors hover:bg-black/[0.1]"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* El cuerpo scrollea solo: en un teléfono chico la imagen + las razones +
            el link no entran en el alto de la pantalla. `min-h-0` es lo que se lo
            permite dentro de un contenedor flex. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          {/* `aspectRatio` inline y no `aspect-[16/10]`: las clases `aspect-[..]`
              arbitrarias no compilan en este proyecto. */}
          <div
            className="w-full overflow-hidden rounded-2xl bg-[#EBEFFE]"
            style={{ aspectRatio: "16 / 10" }}
          >
            <img
              src={IMAGEN}
              alt="Atlas abierto en una computadora"
              className="h-full w-full object-cover"
            />
          </div>

          <h2 className="mt-5 text-[20px] font-semibold leading-tight text-brand-black/90">
            Atlas también está en tu computadora
          </h2>
          <p className="mt-1.5 text-[15px] leading-snug text-[#3C3C43]/60">
            Entrá con tu cuenta actual y vas a encontrar todo lo que ya tenés en
            la versión de celular.
          </p>

          <ul className="mt-5 flex flex-col gap-4">
            <Razon
              icono={<Sparkles className="h-4 w-4" />}
              titulo="Más modos para probar"
              detalle="Desde la compu tenés disponibles modos que en el teléfono no aparecen."
            />
            <Razon
              icono={<Zap className="h-4 w-4" />}
              titulo="Generás más rápido"
              detalle="En la pantalla grande de una computadora se trabaja mucho más cómodo que en el celular."
            />
          </ul>

          {/* El cierre. Sin botón: el link es para leerlo y escribirlo en la otra
              pantalla, no para tocarlo acá. */}
          <div className="mt-6 rounded-2xl border border-neutral-200 bg-[#F9F9F9] p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-[#3C3C43]/60">
              <Monitor className="h-4 w-4 shrink-0" />
              <p className="text-[14px]">En tu computadora andá a</p>
            </div>
            <p className="mt-1.5 break-words text-[17px] font-semibold text-brand-primary">
              “{LINK}”
            </p>
          </div>
        </div>

        {/* Salida explícita: en teléfono la pantalla es completa y una X sola
            arriba a la derecha es poco para cerrarla. Sin `h-*` fijo: el padding
            2:1 es el que define el alto (py-3 / px-6). */}
        <div className="shrink-0 border-t border-neutral-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex w-full items-center justify-center rounded-xl bg-brand-primary px-6 py-3 text-[15px] font-medium text-white shadow-sm transition-colors hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2"
          >
            Listo
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ProbarEnEscritorioModal;
